<template>
  <v-list class="master__navigation text-primary">
    <template v-for="(item, index) in navigation" :key="index">
      <v-list-item
        class="master__step pa-0"
        min-height="32px"
        :class="{ disabled: item.disabled, error: item.error, active: value === index }"
        @click="onNavigationClick(index)"
      >
        <template #prepend>
          <div class="master__step-number">
            <e-loader v-if="loadingIndex === index" />
            <span v-else-if="item.noNumber">...</span>
            <span v-else-if="item.icon" class="master__step-icon"><e-icon :size="20" :name="item.icon" /></span>
            <span v-else>{{ index + 1 }}</span>
          </div>
        </template>
        <p v-if="!$vuetify.display.mdAndDown" class="paragraph-s_h">{{ item.title }}</p>
      </v-list-item>
      <div v-if="index !== navigation.length - 1" class="my-2 master__navigation-divider">
        <img
          v-if="item.disabled || navigation[index + 1].disabled"
          class="d-block"
          src="../../../../assets/img/pages/signin/path.png"
        />
        <div v-else />
      </div>
    </template>
  </v-list>
</template>

<script setup lang="ts">
import { useVModel } from '@vueuse/core';
import { ref } from 'vue';
import type { NavigationItem } from '@/core/types/components/components';
import { EIcon, ELoader } from '@/core/components/ui';

const props = defineProps<{
  modelValue: number;
  navigation: NavigationItem[];
}>();

const value = useVModel(props, 'modelValue');

const emit = defineEmits<{
  (event: 'update:modelValue', index: number): void;
  (event: 'clickNavigation', index: number): void;
}>();

const loadingIndex = ref<number>();

const onNavigationClick = async (index: number) => {
  const item = props.navigation[index];

  if (!item) {
    return;
  }

  loadingIndex.value = index;

  const result = !item.callback || (await item.callback());

  setTimeout(() => {
    loadingIndex.value = undefined;

    if (result) {
      value.value = index;
    }
  });

  emit('clickNavigation', index);
};
</script>

<style scoped lang="scss">
.master {
  &__navigation {
    margin-top: 8px;
    padding: 0;

    @include md-and-down {
      display: flex;
      align-items: center;

      max-width: 635px;
      margin: 0 0 24px;

      img {
        margin: 0 4px;

        transform: rotate(90deg);
      }
    }

    &-divider {
      display: flex;
      justify-content: center;
      align-items: center;

      width: 32px;
      height: 24px;

      div {
        width: 1px;
        height: 100%;

        background-color: $primary-color;
      }

      img {
        height: 100%;
      }

      @include md-and-down {
        width: 24px;
        min-width: 24px;
        margin: 0 8px;

        div {
          width: 100%;
          height: 1px;
        }
      }
    }

    :deep(.v-list-item__content) {
      min-width: 0;
    }
  }

  &__step {
    min-height: 32px;

    font-weight: 500;

    cursor: pointer;

    @include hover {
      color: $btn-primary-pressed-color;
    }

    &.disabled {
      color: $text-inactive-color;

      pointer-events: none;

      .master__step-number {
        color: $text-inactive-color;

        background: url('@/assets/img/pages/signin/num.png') no-repeat center center / contain;
        border-color: transparent;
      }
    }

    &.active {
      color: $primary-color;

      .master__step-number {
        color: $btn-primary-text-color;

        background-color: $btn-primary-bg-color;
      }
    }

    &.error {
      color: $text-alert-color;

      &.active {
        .master__step-number {
          color: $btn-primary-text-color;

          background: $alert-color;
        }
      }

      .master__step-number {
        color: $text-alert-color;

        background: $alert-bg-color;
        border: 1px solid $text-alert-color;
      }
    }

    &-number {
      display: flex;
      justify-content: center;
      align-items: center;

      width: 32px;
      height: 32px;
      margin-right: 12px;

      color: $primary-color;

      background: $primary-tonal-color;
      border: 1px solid $primary-color;
      border-radius: 50%;

      @include md-and-down {
        margin-right: 0;
      }
    }

    &-icon {
      display: flex;
    }

    &:deep(.v-list-item__overlay) {
      display: none;
    }

    &:deep(.v-ripple__container) {
      display: none;
    }
  }
}
</style>
